import Button from "../../UI/Button/Button";
import Cover from "../../UI/Cover/Cover";
import "./CheckOutList.css";
import { ReactElement, useContext, useMemo } from "react";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { coupon_error_message, currency } from "../../utilities/misc";
import { epics_cart_items } from "../../utilities/cartUtilities";
import { CouponObjectContext, LocaleContext } from "../../AppProvider";
import { c_item, e_item } from "../../state_management/epics/order";

type commonProps = {
  coupon: string | null;
  buttonJSX: ReactElement;
  cartTotal: number;
  cartMerged: {
    cart: cartItem[];
    gift_values: cartItem[];
    extras: extrasCartItem[];
  };
};
type readOnlySummary = {
  cartWithCouponTotal: number;
  couponCheck: boolean;
  couponLoading: boolean;
  deactivateBars: true;
};

type editableSummary = {
  cartWithCouponTotal: number;
  couponLoading?: never;
  couponCheck?: never;
  coupon: string | null;
  deactivateBars: false;
};

const CheckOutSummary = ({
  cartMerged,
  coupon,
  cartTotal,
  buttonJSX,
  deactivateBars,
  cartWithCouponTotal,
  couponLoading,
  couponCheck
}: commonProps & (readOnlySummary | editableSummary)) => {
  const { cart, extras, gift_values } = cartMerged;
  const cartCombined = useMemo(() => {
    const merge = (item: cartItem[] | extrasCartItem[]) => {
      return item.map((item) => {
        const service = item.item;
        return {
          discount: service.discount,
          duration: service.duration,
          id: service.id,
          name: service.name,
          photo_path: service.photo_path,
          price: service.price,
          price_with_discount: service.price_with_discount,
          quantity: item.quantity,
          type: item.type
        };
      });
    };
    return [...merge(cart), ...merge(gift_values), ...merge(extras)];
  }, [cartMerged]);

  const { t, locale } = useContext(LocaleContext);
  const { CouponObject } = useContext(CouponObjectContext);
  /* Total before discount */
  const cartMainTotal = useMemo(() => {
    let total = 0;
    for (const item of cartCombined) {
      total = total + item.quantity * item.price;
    }
    return total;
  }, [cartCombined]);

  const { couponRes } = CouponObject;

  const coupon_loading = !couponCheck ? couponLoading : couponRes.loading;

  return (
    <>
      <div className="check_list_wrap">
        {cartCombined.map((service, n) => {
          return (
            <div key={n} className="check_list_item flex">
              <div className="img">
                <div className="checklist_float_indicator indicator">
                  {service.quantity}
                </div>
                <Cover className="">
                  <img src={service.photo_path || ``} alt="" />
                </Cover>
              </div>
              <div className="title flex1 med_text">{service.name}</div>
              <div className="amount med_text">
                {service.discount ? (
                  <div className="strike_through">
                    {currency(
                      (service.quantity * service.price).toFixed(2),
                      locale
                    )}
                  </div>
                ) : (
                  ``
                )}
                <div className="fontBlue">
                  {currency(
                    (service.quantity * service.price_with_discount).toFixed(2),
                    locale,
                    service.type === "gift"
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!couponCheck && deactivateBars ? (
          ``
        ) : (
          <CouponForm
            cart={cart}
            extras={extras}
            gift_values={gift_values}
            couponCheck={couponCheck}
            deactivateBars={deactivateBars}
          />
        )}
      </div>
      <div className="check_list_total">
        <div className="subTotal med_text">
          <div>
            <div className="med_text">{t("Subtotal")}</div>
            <div className="med_text">
              {currency(cartMainTotal.toFixed(2), locale)}
            </div>
          </div>
          <div className="fontBlue">
            <div className="med_text">{t("You save")}</div>
            <div className="med_text">
              - {currency((cartMainTotal - cartTotal).toFixed(2), locale)}
            </div>
          </div>
          {coupon && (
            <div className="fontBlue">
              <div className="med_text">{t("Coupon")}</div>
              <div className="med_text">
                {coupon_loading
                  ? "..."
                  : `- ${currency(
                      Math.abs(cartTotal - cartWithCouponTotal).toFixed(2),
                      locale
                    )}`}
              </div>
            </div>
          )}
        </div>
        <div className="finalTotal">
          <div>
            <div>{t("Total")}</div>
            <div>
              {coupon_loading
                ? "..."
                : currency(cartWithCouponTotal.toFixed(2), locale)}
            </div>
          </div>
        </div>
      </div>
      {buttonJSX}
    </>
  );
};

export default CheckOutSummary;

export const CouponForm = ({
  cart,
  extras,
  gift_values,
  couponCheck,
  deactivateBars,
  isSmall
}: {
  cart: c_item[];
  extras: e_item[];
  gift_values: (c_item & {
    item: {
      price: number;
    };
  })[];
  couponCheck: boolean | undefined;
  deactivateBars: boolean;
  isSmall?: boolean;
}) => {
  const { CouponObject } = useContext(CouponObjectContext);
  const {
    couponRes,
    localCouponValue,
    setShowDiscount,
    showDiscount,
    couponError,
    couponErrorType,
    setLocalCouponValue,
    disabledCouponInput,
    isApplied
  } = CouponObject;

  const { t } = useContext(LocaleContext);

  return !couponCheck && deactivateBars ? (
    <></>
  ) : (
    <div className={`coupon_form_wrapper ${isSmall ? "showSm" : "showLg"}`}>
      <span
        onClick={() => setShowDiscount(!showDiscount)}
        className="coupon_form_span_txt fontBlue med_text"
      >
        {t("Have a discount code?")}
      </span>
      {disabledCouponInput || showDiscount ? (
        <>
          <div className="check_out_discount_code_wrapper flex">
            <input
              type="text"
              disabled={disabledCouponInput}
              readOnly={disabledCouponInput}
              value={localCouponValue || ``}
              onChange={(e) => {
                const value = e.target.value;
                setLocalCouponValue(value);
              }}
            />
            <Button
              type="button"
              disabled={disabledCouponInput || couponRes.loading}
              onClick={(e) => {
                e.preventDefault();
                couponRes.execute({
                  coupon_code: localCouponValue,
                  ...epics_cart_items({
                    cart,
                    extras,
                    gift_values
                  })
                });
              }}
              size="sm"
            >
              {t("Apply")}
            </Button>
          </div>
          <div
            className={`${
              isApplied ? "fontBlue" : "errorMsg"
            } coupon_info_text`}
          >
            {isApplied
              ? t("Applied successfully")
              : couponError && showDiscount && localCouponValue
              ? coupon_error_message(couponErrorType, t)
              : ""}
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
